/**
 * Immutable-ish collection of per-bet stakes. Each bet kind is keyed by its
 * `key` (e.g. "n5", "ganjil", "biru"). Total auto-rolls up.
 */
import type { BetLine } from '../types/api';
import type { BetDef } from '../types/game';

export class BetCollection {
  private readonly map: Map<string, number>;
  private _total = 0;

  private constructor(entries: Iterable<[string, number]>) {
    this.map = new Map(entries);
    for (const v of this.map.values()) this._total += v;
  }

  static empty(): BetCollection {
    return new BetCollection([]);
  }

  copy(): BetCollection {
    return new BetCollection(this.map);
  }

  get isEmpty(): boolean {
    return this.map.size === 0;
  }
  get total(): number {
    return this._total;
  }
  get keys(): string[] {
    return Array.from(this.map.keys());
  }

  amountFor(key: string): number {
    return this.map.get(key) ?? 0;
  }

  add(key: string, amount: number): void {
    if (amount <= 0) return;
    const cur = this.map.get(key) ?? 0;
    const next = cur + amount;
    this.map.set(key, next);
    this._total += amount;
  }

  /** Resolve into wire-format BetLine[] for /action/bet. */
  toBetLines(resolveDef: (key: string) => BetDef | undefined): BetLine[] {
    const out: BetLine[] = [];
    for (const [key, amount] of this.map) {
      const def = resolveDef(key);
      if (!def) continue;
      out.push({ betItemId: def.id, amount });
    }
    return out;
  }

  /** Re-create the same set of bets with all stakes (used for ULANG). */
  asMap(): ReadonlyMap<string, number> {
    return this.map;
  }
}
